"use client";

import type { ReactNode } from "react";
import { CopyButton } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  NOTICE,
  RESULT_CARD,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  paragraphsToHtml,
  paragraphsToPlain,
  type RichParagraph,
} from "@/lib/tool-kit/publishing/rich-text";

/*
 * Presentation shared by the `publishing-statements` family. Like the tool kit, these
 * own layout and never vocabulary: every word arrives through props from the calling
 * widget's strings.
 */

/** Paragraphs with bold and superscript runs. */
export function RichParagraphs({
  paragraphs,
  className = "space-y-2",
}: {
  paragraphs: readonly RichParagraph[];
  className?: string;
}) {
  return (
    <div className={className}>
      {paragraphs.map((paragraph, index) => (
        // Paragraphs are positional output with no identity of their own.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional output
        <p className="break-words text-foreground leading-relaxed" key={index}>
          {paragraph.map((run, runIndex) => {
            const key = `${runIndex}-${run.text}`;
            if (run.sup && run.bold) {
              return (
                <strong key={key}>
                  <sup>{run.text}</sup>
                </strong>
              );
            }
            if (run.sup) {
              return <sup key={key}>{run.text}</sup>;
            }
            if (run.bold) {
              return <strong key={key}>{run.text}</strong>;
            }
            return <span key={key}>{run.text}</span>;
          })}
        </p>
      ))}
    </div>
  );
}

/**
 * A finished statement: heading, the text as it will read, and a copy button that puts
 * both HTML (bold and superscripts survive a paste into Word) and plain text on the
 * clipboard.
 */
export function StatementCard({
  heading,
  paragraphs,
  copyLabel,
  copiedLabel,
  plainSeparator,
  children,
}: {
  heading: string;
  paragraphs: readonly RichParagraph[];
  copyLabel: string;
  copiedLabel: string;
  plainSeparator?: string;
  children?: ReactNode;
}) {
  const list = [...paragraphs];
  return (
    <div className={`${RESULT_CARD} space-y-3`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className={WIDGET_H3}>{heading}</h3>
        <CopyButton
          copiedLabel={copiedLabel}
          html={paragraphsToHtml(list)}
          label={copyLabel}
          small
          text={paragraphsToPlain(list, { paragraphSeparator: plainSeparator })}
        />
      </div>
      <RichParagraphs paragraphs={list} />
      {children}
    </div>
  );
}

/** Advice and warnings under a result — a list, so it is not a `<p>`. */
export function NoticeList({ items }: { items: readonly ReactNode[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className={NOTICE}>
      <ul className="list-disc space-y-1 pl-5">
        {items.map((item, index) => (
          // Notices are computed in a fixed order on every render.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional notices
          <li key={index}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

export function ExampleButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button className={BUTTON_SECONDARY} onClick={onClick} type="button">
      {label}
    </button>
  );
}

/** "Add author", "Remove" and the other row-level actions. */
export function SmallButton({
  label,
  onClick,
  ariaLabel,
  disabled = false,
}: {
  label: string;
  onClick: () => void;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  return (
    <button
      aria-label={ariaLabel}
      className={BUTTON_SMALL}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {label}
    </button>
  );
}

export function Hint({ children }: { children: ReactNode }) {
  return <p className={HINT}>{children}</p>;
}
